'use client';
import React, { useEffect, useState } from 'react';
import { LuNetwork } from 'react-icons/lu';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { Kbd } from '@core/ui/primitives/kbd';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@core/ui/primitives/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@core/ui/primitives/dialog';
import { Button } from '@/core/ui/primitives/button';
import MindmapCanvas from './MindmapCanvas';
import { loadMindmap } from '../core/MindmapPersistence';
import type { Mindmap } from '../core/MindmapTypes';
interface MindmapDialogTriggerProps {
  className?: string;
}
export function MindmapDialogTrigger({ className = '' }: MindmapDialogTriggerProps) {
  const [open, setOpen] = useState(false);
  const [mindmap, setMindmap] = useState<Mindmap | null>(null);
  const [isMac, setIsMac] = useState(false);
  const shortcutKey = isMac ? '⌘' : 'Ctrl';
  useEffect(() => {
    if (typeof navigator === 'undefined') {
      return;
    }
    const platform = navigator.platform ?? '';
    const macPlatform = /mac|ipad|iphone|ipod/i.test(platform);
    setIsMac(macPlatform);
  }, []);
  useEffect(() => {
    // Lazy load current active mindmap from local storage
    try {
      const m = loadMindmap();
      setMindmap(m);
    } catch {
      setMindmap(null);
    }
  }, [open]);
  // Render canvas immediately (no deferred mount)
  // Global events: toggle/open
  useEffect(() => {
    if (typeof window === 'undefined') {
      return () => {};
    }
    const onOpen = () => setOpen(true);
    const onToggle = () => setOpen((prev) => !prev);
    window.addEventListener('mindmap:open', onOpen);
    window.addEventListener('hotkey:mindmap.toggle', onToggle);
    return () => {
      window.removeEventListener('mindmap:open', onOpen);
      window.removeEventListener('hotkey:mindmap.toggle', onToggle);
    };
  }, []);
  return (
    <>
      <TooltipProvider>
        <Tooltip delayDuration={60}>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => setOpen(true)}
              className={cn(
                'flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-transparent transition-colors hover:bg-primary/10',
                className
              )}
              aria-label={`Mindmap (${shortcutKey} + M)`}>
              <LuNetwork className='w-4 h-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Mindmap ({shortcutKey} M)</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className={cn(
            'p-0 sm:rounded-xl shadow-xl outline-none',
            'max-w-none sm:max-w-none',
            'w-[calc(100vw-2rem)] h-[calc(100vh-2rem)]'
          )}
          onEscapeKeyDown={(event: KeyboardEvent) => {
            const t = event.target as HTMLElement | null;
            const tag = (t?.tagName || '').toUpperCase();
            const editable = (t as any)?.isContentEditable === true;
            const inLocalScope = !!t?.closest('[data-hotkey-scope="local"]');
            if (inLocalScope || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || editable) {
              event.preventDefault();
            }
          }}>
          <DialogHeader className='sr-only'>
            <DialogTitle>Mindmap</DialogTitle>
            <DialogDescription>Interactive mindmap canvas</DialogDescription>
          </DialogHeader>
          <div className='relative h-full w-full'>
            {mindmap ? (
              <MindmapCanvas mindmap={mindmap} />
            ) : (
              <div className='flex h-full w-full items-center justify-center text-muted-foreground'>
                Loading mindmap…
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
export default MindmapDialogTrigger;
